import React, { useState, useEffect } from 'react';
import Navbar from "../../component/HeaderData";
import Footer from "../../component/FooterData";
import BackButton from '../../component/Common/BackBtn';
import { useMsal } from '@azure/msal-react';
import { useHistory } from 'react-router-dom';
import { Bar } from 'react-chartjs-2';
import { Line } from 'react-chartjs-2';
import { Pie } from 'react-chartjs-2';
import PageSubTitle from '../../component/Common/PageSubTitle';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, PointElement, LineElement, TooltipItem, ArcElement } from 'chart.js';
import { useReportInventoryService } from '../../services/ReportInventoryServices';
import { useLooksUpData } from '../../../src/utils/getLookupTableData';
import { toast } from 'react-toastify';
import dashboardIcon from '../../img/usa-icons/report.svg'
import reports from '../../assets/images/reports.svg'
import referenceIcon from '../../img/usa-icons/folder.svg'



ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);
ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend);
ChartJS.register(Tooltip, Legend, ArcElement);

const Dashboard = () => {
    const { instance, accounts } = useMsal();
    const history = useHistory();
    const [isLoading, setIsLoading] = useState(false);
    const ReportInventoryService = useReportInventoryService();
    const [inventoryReportResponseList, setInventoryReportResponseList] = useState<any[]>([]);
    const { getMasterTableLookupValueData, getMasterTableLookupData } = useLooksUpData();
    const [selectedReportCountData, setSelectedReportCountData] = useState<any>(null);
    const [selectedReportScoreData, setSelectedReportScoreData] = useState<any>(null);
    const [showMainButtons, setShowMainButtons] = useState(false);
    const [showSubButtons, setShowSubButtons] = useState(false);
    const [showFinalButtons, setShowFinalButtons] = useState(false);
    const [showFinalButtonsValue, setShowFinalButtonsValue] = useState(false);
    const [isSubmitted, setIsSubmitted] = useState(false);
    const userType = sessionStorage.getItem('signedInUserType');

    // Separate states for each chart
    const [reportCountChartData, setReportCountChartData] = useState<any>({
        labels: [],
        datasets: [],
    });
    const [reportScoreChartData, setReportScoreChartData] = useState<any>({
        labels: [],
        datasets: [],
    });
    const [submissionNumberChartData, setSubmissionNumberChartData] = useState<any>({
        labels: [],
        datasets: [],
    });
    const [lastSubmissionStatusChartData, setLastSubmissionStatusChartData] = useState<any>({
        labels: [],
        datasets: [],
    });

    const [lastReviewStatusChartData, setLastReviewStatusChartData] = useState<any>({
        labels: [],
        datasets: [],
    });

    const healthCareRouteFn = async () => {
        history.push('/dashboard-health-care-quality');
    };

    const intakeDataQualityRouteFn = async () => {
        history.push('/dashboard-intake-data-quality');
    };

    const healthQualityRouteFn = async () => {
        history.push('/dashboard-health-quality');
    };

    const [totalPlanScore, setTotalPlanScore] = useState(0);

    useEffect(() => {
        const timeoutId = setTimeout(() => {
            if (userType === null && (!accounts?.[0]?.localAccountId)) {
                history.push('/sign-in');
            }
        }, 1000);

        return () => clearTimeout(timeoutId);
    }, [accounts, history, userType]);

    const fetchReportInventoryData = async (year: any, quarter: any, month: any) => {
        setIsLoading(true);
        try {
            const response = await ReportInventoryService.getAllReportsDashboard(year, quarter, month);
            const reportScroeResponse = await ReportInventoryService.getAllReportsScoreDashboard(year, quarter, month);
            const lookupDataResponse = await getMasterTableLookupData();
            const masterLookupDataResponse = await getMasterTableLookupValueData();

            if (response && lookupDataResponse && masterLookupDataResponse) {
                // setInventoryReportResponseList(response.data);

                const filterAndMapData = (group: string, table: string, column: string) => {
                    return masterLookupDataResponse.data
                        .filter((dt: any) => dt.lkpcd_group === group && dt.table_name === table && dt.column_name === column)
                        .map((code: any) => ({
                            lkpdmn_lkpcd: `${code.lkpdmn_lkpcd}`,
                            lkdmn_lkpcd_desc: code.lkdmn_lkpcd_desc,
                            lkpcd_group: code.lkpcd_group,
                        }));
                };

                const mcBusinessStatusLKPCD = filterAndMapData('NG', 'MCT_REPORT_INSTANCE', 'LAST_SUBMSN_STATUS_LKPCD');
                const mcAfStatusLKPCD = filterAndMapData('NG', 'MCT_REPORT_INSTANCE', 'LAST_REVIEW_STATUS_LKPCD');

                // Group data by plan_id and current_submission_number
                const reportGroupedData = response.data.reduce((acc: Record<string, { totalCount: number }>, record: any) => {
                    const planId = record.plan_id;

                    if (!acc[planId]) {
                        acc[planId] = { totalCount: 0 };
                    }
                    acc[planId].totalCount += 1; // Increment total count for the plan_id
                    return acc;
                }, {});

                // Filter mc_plan based on plan IDs in grouped data
                const filteredMcPlan = lookupDataResponse.data.mc_plan.filter((item: { planId: string }) =>
                    Object.keys(reportGroupedData).includes(item.planId)
                );

                // Define a mapping for short labels
                const planNameMapping: { [key: string]: string } = {
                    "UnitedHealthcare Community Plan of Nebraska": "UHC",
                    "Nebraska Total Care": "NTC",
                    "Molina Healthcare of Nebraska": "MHN",
                };

                // Generate labels for the charts (plan names)
                const labels = filteredMcPlan.map((item: { planId: string; planName: string }) =>
                    planNameMapping[item.planName] || item.planName // Use short name if available, otherwise fallback to full name
                );

                // Generate data for the total count of plans chart
                const totalCountData = Object.keys(reportGroupedData).map((planId) => reportGroupedData[planId].totalCount);

                // Update chart data states
                setReportCountChartData({
                    labels: labels,
                    datasets: [
                        {
                            label: 'Total Count of Reports',
                            data: totalCountData,
                            backgroundColor: 'rgba(75, 192, 192, 0.2)',
                            borderColor: 'rgb(45, 115, 115)',
                            borderWidth: 1,
                        },
                    ],
                });

                // Report Score Chart
                // Process the report score data
                const reportScoreGroupedData = Object.entries(reportScroeResponse.data).map(([planId, score]) => ({
                    planId,
                    score,
                }));

                // Calculate the total score
                const totalScore = reportScoreGroupedData.reduce((sum, item: { planId: string; score: unknown }) => sum + (item.score as number), 0);
                setTotalPlanScore(totalScore); // Update the total score state

                // Generate labels and data for the bar chart
                const reportScoreLabels = filteredMcPlan.map((item: { planId: string; planName: string }) =>
                    planNameMapping[item.planName] || item.planName // Use short name if available, otherwise fallback to full name
                );
                const reportScoreData = reportScoreGroupedData.map((item) => item.score);

                // Update the chart state
                setReportScoreChartData({
                    labels: reportScoreLabels,
                    datasets: [
                        {
                            label: 'Report Score',
                            data: reportScoreData,
                            backgroundColor: 'rgba(23, 78, 107, 0.2)',
                            borderColor: 'rgb(37, 90, 127)',
                            borderWidth: 1,
                        },
                    ],
                });

                //submission numbers chart
                const submissionNumberGroupedData = response.data.reduce((acc: Record<string, { totalCount: number; submissionNumbers: Record<number, number> }>, record: any) => {
                    const planId = record.plan_id;
                    const submissionNumber = record.current_submission_number || 0;

                    if (!acc[planId]) {
                        acc[planId] = { totalCount: 0, submissionNumbers: {} };
                    }
                    acc[planId].totalCount += 1; // Increment total count for the plan_id
                    if (!acc[planId].submissionNumbers[submissionNumber]) {
                        acc[planId].submissionNumbers[submissionNumber] = 0;
                    }
                    acc[planId].submissionNumbers[submissionNumber] += 1; // Count occurrences of each submission number
                    return acc;
                }, {});

                // Generate data for submission numbers chart
                const submissionNumberLabels: number[] = [
                    ...Array.from(new Set(response.data.map((record: any) => record.current_submission_number || 0))) as number[],
                ].sort((a, b) => a - b);

                const submissionNumberDatasets = filteredMcPlan.map((item: { planId: string; planName: string }) => {
                    const planId = item.planId;
                    const planName = planNameMapping[item.planName] || item.planName;

                    const data = submissionNumberLabels.map(
                        (submissionNumber: number) => submissionNumberGroupedData[planId].submissionNumbers[submissionNumber] || 0
                    );

                    return {
                        label: planName,
                        data: data,
                        borderColor: `rgba(${Math.floor(Math.random() * 255)}, ${Math.floor(
                            Math.random() * 255
                        )}, ${Math.floor(Math.random() * 255)}, 1)`,
                        backgroundColor: `rgba(${Math.floor(Math.random() * 255)}, ${Math.floor(
                            Math.random() * 255
                        )}, ${Math.floor(Math.random() * 255)}, 0.2)`,
                        borderWidth: 2,
                        fill: false,
                    };
                });

                setSubmissionNumberChartData({
                    labels: submissionNumberLabels,
                    datasets: submissionNumberDatasets,
                });

                // Last submission status chart
                const lastSumissionStatusGroupedData = response.data.reduce((acc: Record<string, Record<string, number>>, record: any) => {
                    const planId = record.plan_id;
                    const status = record.last_submission_status || "Unknown";

                    if (!acc[planId]) {
                        acc[planId] = {};
                    }
                    if (!acc[planId][status]) {
                        acc[planId][status] = 0;
                    }
                    acc[planId][status] += 1; // Count occurrences of each status
                    return acc;
                }, {});

                // Generate labels for the x-axis (status values)
                const statusLabels: string[] = [
                    ...Array.from(new Set(response.data.map((record: any) => record.last_submission_status || "Unknown"))).map(String),
                ].sort((a: string, b: string) => {
                    // Sort numerically if the values are numbers, otherwise sort alphabetically
                    const numA = parseFloat(a);
                    const numB = parseFloat(b);
                    if (!isNaN(numA) && !isNaN(numB)) {
                        return numA - numB; // Numeric sort
                    }
                    return a.localeCompare(b); // Alphabetical sort for non-numeric values
                });

                // Generate datasets for each plan
                const lastSubmissionStatusDatasets = filteredMcPlan.map((item: { planId: string; planName: string }) => {
                    const planId = item.planId;
                    const planName = planNameMapping[item.planName] || item.planName;

                    const data = statusLabels.map(
                        (status: string) => lastSumissionStatusGroupedData[planId]?.[status] || 0
                    );

                    return {
                        label: planName, // Plan name as the label
                        data: data,
                        borderColor: `rgba(${Math.floor(Math.random() * 255)}, ${Math.floor(
                            Math.random() * 255
                        )}, ${Math.floor(Math.random() * 255)}, 1)`,
                        backgroundColor: `rgba(${Math.floor(Math.random() * 255)}, ${Math.floor(
                            Math.random() * 255
                        )}, ${Math.floor(Math.random() * 255)}, 0.2)`,
                        borderWidth: 2,
                        fill: false, // Do not fill under the line
                    };
                });

                setLastSubmissionStatusChartData({
                    labels: statusLabels.map((status) => {
                        // Ensure matching by trimming and converting to lowercase
                        const matchingStatus = mcBusinessStatusLKPCD.find(
                            (item: any) => item.lkpdmn_lkpcd.trim() === status.toString().trim()
                        );

                        // Return the description if found, otherwise fallback to the raw status
                        return matchingStatus ? matchingStatus.lkdmn_lkpcd_desc : `${status}`;
                    }),
                    datasets: lastSubmissionStatusDatasets,
                });

                // Review status chart
                // Group data by plan_id and last_submission_status
                const groupedReviewStatusData = response.data.reduce((acc: Record<string, Record<string, number>>, record: any) => {
                    const planId = record.plan_id;
                    const status = record.last_review_status || "Not in review";

                    if (!acc[planId]) {
                        acc[planId] = {};
                    }
                    if (!acc[planId][status]) {
                        acc[planId][status] = 0;
                    }
                    acc[planId][status] += 1; // Count occurrences of each status
                    return acc;
                }, {});

                const reviewStatusLabels: string[] = [
                    ...Array.from(new Set(response.data.map((record: any) => record.last_review_status || "Not in review"))).map(String),
                ].sort((a: string, b: string) => {
                    // Sort numerically if the values are numbers, otherwise sort alphabetically
                    const numA = parseFloat(a);
                    const numB = parseFloat(b);
                    if (!isNaN(numA) && !isNaN(numB)) {
                        return numA - numB; // Numeric sort
                    }
                    return a.localeCompare(b); // Alphabetical sort for non-numeric values
                });

                // Generate datasets for each plan
                const lastReviewStatusDatasets = filteredMcPlan.map((item: { planId: string; planName: string }) => {
                    const planId = item.planId;
                    const planName = planNameMapping[item.planName] || item.planName;

                    const data = reviewStatusLabels.map(
                        (status: string) => groupedReviewStatusData[planId]?.[status] || 0
                    );

                    return {
                        label: planName, // Plan name as the label
                        data: data,
                        borderColor: `rgba(${Math.floor(Math.random() * 255)}, ${Math.floor(
                            Math.random() * 255
                        )}, ${Math.floor(Math.random() * 255)}, 1)`,
                        backgroundColor: `rgba(${Math.floor(Math.random() * 255)}, ${Math.floor(
                            Math.random() * 255
                        )}, ${Math.floor(Math.random() * 255)}, 0.2)`,
                        borderWidth: 2,
                        fill: false, // Do not fill under the line
                    };
                });

                // Update chart data state
                setLastReviewStatusChartData({
                    labels: reviewStatusLabels.map((status) => {
                        // Ensure matching by trimming and converting to lowercase
                        const matchingStatus = mcAfStatusLKPCD.find(
                            (item: any) => item.lkpdmn_lkpcd.trim() === status.toString().trim()
                        );

                        // Return the description if found, otherwise fallback to the raw status
                        return matchingStatus ? matchingStatus.lkdmn_lkpcd_desc : `${status}`;
                    }),
                    datasets: lastReviewStatusDatasets,
                });
            } else {
                toast.error("No data found.");
            }
        } catch (error: any) {
            console.error('Error fetching report inventory data:', error);
            if (error.response && error.response.status === 500 && error.response.data.message.includes("Invalid quarter")) {
                toast.error("Invalid quarter.");
            } else {
                toast.error("An error occurred while fetching data.");
            }
        } finally {
            setIsLoading(false);
        }
    };

    const barChartOptions = {
        responsive: true,
        plugins: {
            legend: {
                position: 'top' as const,
                display: true,
            },
            title: {
                display: true,
                text: 'Bar Chart',
            },
        },
    };
    // State for year and quarter
    const [selectedYear, setSelectedYear] = useState(2025);
    const [selectedQuarter, setSelectedQuarter] = useState("All");
    const [selectedMonth, setSelectedMonth] = useState("All");

    const handleSubmit = () => {
        setIsSubmitted(true);
        setReportCountChartData({
            labels: [],
            datasets: [],
        });

        setShowMainButtons(false);
        setShowSubButtons(false);
        setShowFinalButtons(false);
        setShowFinalButtonsValue(false);
        setSelectedReportScoreData(null);
        setSelectedReportCountData(null);

        fetchReportInventoryData(
            selectedYear,
            selectedQuarter === "All" ? 'all' : selectedQuarter,
            selectedMonth === "All" ? 'all' : selectedMonth).then(() => {
                setShowMainButtons(true);
            });
    };

    const handleReportCountRowClick = (label: string, value: number) => {
        setSelectedReportScoreData(null);
        setSelectedReportCountData({
            labels: [label],
            datasets: [
                {
                    label: 'Report Count Details',
                    data: [value],
                    backgroundColor: 'rgba(75, 192, 192, 0.2)',
                    borderColor: 'rgba(75, 192, 192, 1)',
                    borderWidth: 1,
                },
            ],
        });
    };

    const handleReportScoreRowClick = (label: string, value: number) => {
        setSelectedReportCountData(null);
        setSelectedReportScoreData({
            labels: [label],
            datasets: [
                {
                    label: 'Report Score Details',
                    data: [value],
                    backgroundColor: 'rgba(23, 78, 107, 0.2)',
                    borderColor: 'rgb(37, 90, 127)',
                    borderWidth: 1,
                },
            ],
        });
    };

    const DataTable = ({ labels, data, title, onRowClick }: { labels: string[]; data: number[]; title: string; onRowClick: (label: string, value: number) => void }) => {
        return (
            <div style={{ marginTop: '20px', textAlign: 'center' }}>
                <h3 style={{ fontSize: '16px', fontWeight: 'bold', marginBottom: '10px' }}>{title}</h3>
                <table style={{ width: '100%', borderCollapse: 'collapse', margin: '0 auto' }}>
                    <thead>
                        <tr>
                            <th style={{ border: '1px solid #ccc', padding: '8px', backgroundColor: '#f9f9f9' }}>Plan</th>
                            <th style={{ border: '1px solid #ccc', padding: '8px', backgroundColor: '#f9f9f9' }}>Value</th>
                            <th style={{ border: '1px solid #ccc', padding: '8px', backgroundColor: '#f9f9f9' }}>Action</th>
                        </tr>
                    </thead>
                    <tbody>
                        {labels.map((label, index) => (
                            <tr key={index}>
                                <td style={{ border: '1px solid #ccc', padding: '8px' }}>{label}</td>
                                <td style={{ border: '1px solid #ccc', padding: '8px' }}>{data[index]}</td>
                                <td style={{ border: '1px solid #ccc', padding: '8px' }}>
                                    <button
                                        onClick={() => onRowClick(label, data[index])}
                                        style={{
                                            padding: '5px 10px',
                                            backgroundColor: '#007bff',
                                            color: '#fff',
                                            border: 'none',
                                            borderRadius: '5px',
                                            cursor: 'pointer',
                                        }}
                                    >
                                        Details
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        );
    };

    const handleMainButtonClick = () => {
        setShowSubButtons(true);
    };

    const handleFinalButtonClick = () => {
        setShowFinalButtonsValue(true);
    };

    const handleSubButtonClick = () => {
        setShowFinalButtons(true);
    };


    const pieChartData = {
        NTC: {
            labels: ['60%', '40%'],
            datasets: [
                {
                    data: [60, 40],
                    backgroundColor: ['#2196f3','#bbdefb' ],
                    borderWidth: 1,
                },
            ],
        },
        UHC: {
            labels: ['75%', '25%'],
            datasets: [
                {
                    data: [75, 25],
                    backgroundColor: ['#4caf50','#a5d6a7' ],
                    borderWidth: 1,
                },
            ],
        },
        MHN: {
            labels: ['45%', '55%'],
            datasets: [
                {
                    data: [45, 55],
                    backgroundColor: ['#ff9800', '#FFD180'], // Purple and Light Blue
                    borderWidth: 1,
                },
            ],
        },
    };

    // Options for the pie charts
    const pieChartOptions = {
        responsive: true,
        plugins: {
            legend: {
                position: 'top', // Position of the legend
            },
            // tooltip: {
            //     callbacks: {
            //         label: (tooltipItem) => {
            //             const rawValue = tooltipItem.raw as number;
            //             return `${tooltipItem.label}: ${rawValue}%`;
            //         },
            //     },
            // },
        },
    };

    return (
        <>
            <Navbar />
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                <div style={{ alignSelf: 'flex-start' }}>
                    <BackButton />
                </div>
                <div style={{ width: '100%', display: 'flex', justifyContent: 'center', marginTop: '20px' }}>
                    <PageSubTitle text="Plan Performance Excellence Scoreboard" />
                </div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'center', marginTop: '5px' }}>
                <h3 style={{ fontSize: '18px', fontWeight: 'bold' }}>Select Year</h3>
            </div>
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '20px', marginTop: '10px' }}>
                <select
                    value={selectedYear}
                    onChange={(e) => setSelectedYear(Number(e.target.value))}
                    style={{
                        marginRight: '10px',
                        padding: '8px 12px',
                        border: '1px solid #ccc',
                        borderRadius: '5px',
                        fontSize: '14px',
                        backgroundColor: '#f9f9f9',
                        cursor: 'pointer',
                        outline: 'none',
                        transition: 'border-color 0.3s',
                    }}
                    onFocus={(e) => (e.target.style.borderColor = '#007bff')}
                    onBlur={(e) => (e.target.style.borderColor = '#ccc')}
                >
                    <option value="All">All</option>
                    <option value={2025}>2025</option>
                    <option value={2024}>2024</option>

                </select>

            </div>
            <div style={{ display: 'flex', justifyContent: 'center', flexWrap: 'wrap', gap: '20px', marginTop: '20px' }}>
                {/* NTC Pie Chart */}
                <div style={{ width: '300px', textAlign: 'center' }}>
                    <h4>NTC</h4>
                    <Pie
                        data={pieChartData.NTC}
                    />
                </div>

                {/* UHC Pie Chart */}
                <div style={{ width: '300px', textAlign: 'center' }}>
                    <h4>UHC</h4>
                    <Pie
                        data={pieChartData.UHC}
                    />
                </div>

                {/* MHN Pie Chart */}
                <div style={{ width: '300px', textAlign: 'center' }}>
                    <h4>MHN</h4>
                    <Pie
                        data={pieChartData.MHN}
                    />
                </div>
            </div>
            {accounts?.[0]?.localAccountId && (
                <div style={{ display: 'flex', justifyContent: 'center', gap: '20px', marginTop: '20px' }}>
                    <div
                        onClick={intakeDataQualityRouteFn}
                        className={`border-[7px] border-[#bac6de] cursor-pointer text-primary md:text-lg text-md md:font-medium font-normal rounded-3xl md:py-5 py-2 px-2 w-80 flex flex-row items-start justify-between`}
                        style={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: '#f9f9f9',
                            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                            transition: 'transform 0.2s',
                            cursor: 'pointer',
                        }}
                        onMouseEnter={(e) => (e.currentTarget.style.transform = 'scale(1.05)')}
                        onMouseLeave={(e) => (e.currentTarget.style.transform = 'scale(1)')}
                    >
                        <img src={reports} alt="lock" width="100px" className="self-start" />
                        <div className="space-y-2 mb-16 flex flex-col justify-between ml-2">
                            <p className="leading-5 mb-3 text-black">Intake Process Scores</p>
                            <div className="flex gap-2">
                                <p className="text-xs">
                                    These performance measures demonstrate how well the plans submit the required reports for necessary compliance and ensure accuracy.
                                </p>
                            </div>
                        </div>
                    </div>
                    <div
                        onClick={healthCareRouteFn}
                        className={`border-[7px] border-[#bac6de] cursor-pointer text-primary md:text-lg text-md md:font-medium font-normal rounded-3xl md:py-5 py-2 px-2 w-80 flex flex-row items-start justify-between`}
                        style={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: '#f9f9f9',
                            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                            transition: 'transform 0.2s',
                            cursor: 'pointer',
                        }}
                        onMouseEnter={(e) => (e.currentTarget.style.transform = 'scale(1.05)')}
                        onMouseLeave={(e) => (e.currentTarget.style.transform = 'scale(1)')}
                    >
                        <img src={dashboardIcon} alt="lock" width="100px" className="self-start" />
                        <div className="space-y-2 mb-16 flex flex-col justify-between ml-2">
                            <p className="leading-5 mb-3 text-black">Operations Quality Scores</p>
                            <div className="flex gap-2">
                                <p className="text-xs">
                                    These performance measures show how well the plans support people in their community while ensuring system sustainability.
                                </p>
                            </div>
                        </div>
                    </div>
                    <div
                        onClick={healthQualityRouteFn}
                        className={`border-[7px] border-[#bac6de] cursor-pointer text-primary md:text-lg text-md md:font-medium font-normal rounded-3xl md:py-5 py-2 px-2 w-80 flex flex-row items-start justify-between`}
                        style={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: '#f9f9f9',
                            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                            transition: 'transform 0.2s',
                            cursor: 'pointer',
                        }}
                        onMouseEnter={(e) => (e.currentTarget.style.transform = 'scale(1.05)')}
                        onMouseLeave={(e) => (e.currentTarget.style.transform = 'scale(1)')}
                    >
                        <img src={referenceIcon} alt="lock" width="100px" className="self-start" />
                        <div className="space-y-2 mb-16 flex flex-col justify-between ml-2">
                            <p className="leading-5 mb-3 text-black">Health Quality Scores</p>
                            <div className="flex gap-2">
                                <p className="text-xs">
                                These performance measures  demonstrate how well the plans provided services affecting their community 's health quality.

                                </p>
                            </div>
                        </div>
                    </div>
                    
                </div>
            )}

            {/* Dropdowns and Submit Button */}
            {/* <div style={{ display: 'flex', justifyContent: 'center', marginTop: '5px' }}>
                <h3 style={{ fontSize: '18px', fontWeight: 'bold' }}>Select Year and Quarter:</h3>
            </div>
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '20px', marginTop: '10px' }}>
                <select
                    value={selectedYear}
                    onChange={(e) => setSelectedYear(Number(e.target.value))}
                    style={{
                        marginRight: '10px',
                        padding: '8px 12px',
                        border: '1px solid #ccc',
                        borderRadius: '5px',
                        fontSize: '14px',
                        backgroundColor: '#f9f9f9',
                        cursor: 'pointer',
                        outline: 'none',
                        transition: 'border-color 0.3s',
                    }}
                    onFocus={(e) => (e.target.style.borderColor = '#007bff')}
                    onBlur={(e) => (e.target.style.borderColor = '#ccc')}
                >
                    {Array.from({ length: 11 }, (_, i) => 2020 + i).map((year) => (
                        <option key={year} value={year}>
                            {year}
                        </option>
                    ))}
                </select>

                <select
                    value={selectedQuarter}
                    onChange={(e) => setSelectedQuarter(e.target.value)}
                    style={{
                        marginRight: '10px',
                        padding: '8px 12px',
                        border: '1px solid #ccc',
                        borderRadius: '5px',
                        fontSize: '14px',
                        backgroundColor: '#f9f9f9',
                        cursor: 'pointer',
                        outline: 'none',
                        transition: 'border-color 0.3s',
                    }}
                    onFocus={(e) => (e.target.style.borderColor = '#007bff')}
                    onBlur={(e) => (e.target.style.borderColor = '#ccc')}
                >
                    <option value="All">All</option>
                    <option value="1">Q1</option>
                    <option value="2">Q2</option>
                    <option value="3">Q3</option>
                    <option value="4">Q4</option>
                </select>

                <select
                    value={selectedMonth}
                    onChange={(e) => setSelectedMonth(e.target.value)}
                    style={{
                        marginRight: '10px',
                        padding: '8px 12px',
                        border: '1px solid #ccc',
                        borderRadius: '5px',
                        fontSize: '14px',
                        backgroundColor: '#f9f9f9',
                        cursor: 'pointer',
                        outline: 'none',
                        transition: 'border-color 0.3s',
                    }}
                    onFocus={(e) => (e.target.style.borderColor = '#007bff')}
                    onBlur={(e) => (e.target.style.borderColor = '#ccc')}
                >
                    <option value="All">All</option>
                    <option value="1">1</option>
                    <option value="2">2</option>
                    <option value="3">3</option>
                    <option value="4">4</option>
                    <option value="5">5</option>
                    <option value="6">6</option>
                    <option value="7">7</option>
                    <option value="8">8</option>
                    <option value="9">9</option>
                    <option value="10">10</option>
                    <option value="11">11</option>
                    <option value="12">12</option>
                </select>

                <button
                    onClick={handleSubmit}
                    style={{
                        padding: '8px 16px',
                        backgroundColor: '#007bff',
                        color: '#fff',
                        border: 'none',
                        borderRadius: '5px',
                        fontSize: '14px',
                        cursor: 'pointer',
                        transition: 'background-color 0.3s',
                    }}
                    onMouseEnter={(e) => ((e.target as HTMLButtonElement).style.backgroundColor = '#0056b3')}
                    onMouseLeave={(e) => ((e.target as HTMLButtonElement).style.backgroundColor = '#007bff')}
                >
                    Submit
                </button>
            </div> */}

            {/* Main Buttons 
            <div className="form-layout">
                {isLoading ? (
                    <div className="loading-container">
                        <div className="loading-spinner"></div>
                        <p>Loading.....</p>
                    </div>
                ) : (
                    <div className="form-layout">
                        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%', flexWrap: 'wrap', marginBottom: '20px' }}>
                            {showMainButtons && (
                                <div style={{ marginBottom: '20px' }}>
                                    <button
                                        onClick={handleMainButtonClick}
                                        style={{
                                            padding: '10px 20px',
                                            backgroundColor: '#28a745',
                                            color: '#fff',
                                            border: 'none',
                                            borderRadius: '5px',
                                            cursor: 'pointer',
                                            marginRight: '10px',
                                        }}
                                    >
                                        Health Care Quality
                                    </button>
                                    <button
                                        onClick={handleMainButtonClick}
                                        style={{
                                            padding: '10px 20px',
                                            backgroundColor: '#28a745',
                                            color: '#fff',
                                            border: 'none',
                                            borderRadius: '5px',
                                            cursor: 'pointer',
                                        }}
                                    >
                                        Intake Data Quality
                                    </button>
                                </div>
                            )}
                        </div>

                        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%', flexWrap: 'wrap', marginBottom: '20px' }}>
                            {showSubButtons && (
                                <div style={{ marginBottom: '20px' }}>
                                    <button
                                        onClick={handleSubButtonClick}
                                        style={{
                                            padding: '10px 20px',
                                            backgroundColor: '#ffc107',
                                            color: '#fff',
                                            border: 'none',
                                            borderRadius: '5px',
                                            cursor: 'pointer',
                                            marginRight: '10px',
                                        }}
                                    >
                                        Grievances
                                    </button>
                                    <button
                                        onClick={handleSubButtonClick}
                                        style={{
                                            padding: '10px 20px',
                                            backgroundColor: '#ffc107',
                                            color: '#fff',
                                            border: 'none',
                                            borderRadius: '5px',
                                            cursor: 'pointer',
                                            marginRight: '10px',
                                        }}
                                    >
                                        Intake QualityA
                                    </button>
                                    <button
                                        onClick={handleSubButtonClick}
                                        style={{
                                            padding: '10px 20px',
                                            backgroundColor: '#ffc107',
                                            color: '#fff',
                                            border: 'none',
                                            borderRadius: '5px',
                                            cursor: 'pointer',
                                        }}
                                    >
                                        Intake QualityB
                                    </button>
                                </div>
                            )}

                        </div>

                        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%', flexWrap: 'wrap', marginBottom: '20px' }}>
                            {showFinalButtons && (
                                <div>
                                    <button
                                        onClick={handleFinalButtonClick}
                                        style={{
                                            padding: '10px 20px',
                                            backgroundColor: '#dc3545',
                                            color: '#fff',
                                            border: 'none',
                                            borderRadius: '5px',
                                            cursor: 'pointer',
                                            marginRight: '10px',
                                        }}
                                    >
                                        Timeliness
                                    </button>
                                    <button
                                        onClick={handleFinalButtonClick}
                                        style={{
                                            padding: '10px 20px',
                                            backgroundColor: '#dc3545',
                                            color: '#fff',
                                            border: 'none',
                                            borderRadius: '5px',
                                            cursor: 'pointer',
                                        }}
                                    >
                                        Intake Compliance
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>

            <div className="form-layout">
                {isLoading ? (
                    <div className="loading-container">
                        <div className="loading-spinner"></div>
                        <p>Loading.....</p>
                    </div>
                ) : isSubmitted && reportCountChartData.labels.length === 0 ? (
                    <div style={{ textAlign: 'center', marginTop: '20px' }}>
                        <h3>No data available for the selected year and quarter.</h3>
                    </div>
                ) : showFinalButtonsValue && (
                    <div className="form-layout">
                        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%', flexWrap: 'wrap', marginBottom: '20px' }}>
                            <div style={{ flex: '1 1 100%', maxWidth: '500px', margin: '0 1px', border: '1px solid #ccc', borderRadius: '5px', padding: '1px', marginInline: '10px' }}>
                                <DataTable
                                    labels={reportCountChartData.labels}
                                    data={reportCountChartData.datasets[0].data}
                                    title="Report Counts by Plan"
                                    onRowClick={handleReportCountRowClick}
                                />
                            </div>

                            <div style={{ flex: '1 1 100%', maxWidth: '500px', margin: '0 1px', border: '1px solid #ccc', borderRadius: '5px', padding: '1px', marginInline: '10px' }}>
                                <DataTable
                                    labels={reportScoreChartData.labels}
                                    data={reportScoreChartData.datasets[0].data}
                                    title="Report Scores by Plan"
                                    onRowClick={handleReportScoreRowClick}
                                />
                            </div>
                        </div>

                        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%', flexWrap: 'wrap', marginBottom: '20px' }}>
                            {selectedReportCountData && (
                                <div style={{ flex: '1 1 100%', maxWidth: '500px', margin: '0 1px', border: '1px solid #ccc', borderRadius: '5px', padding: '1px', marginInline: '10px' }}>
                                    <Bar
                                        data={reportCountChartData}
                                        options={{
                                            ...barChartOptions,
                                            plugins: {
                                                ...barChartOptions.plugins,
                                                title: {
                                                    display: true, text: 'Report Counts by Plan',
                                                    font: {
                                                        weight: 'bold',
                                                    },
                                                    color: 'black',
                                                },
                                            },
                                        }}
                                    />
                                </div>
                            )}


                            {selectedReportScoreData && (
                                <div style={{ flex: '1 1 100%', maxWidth: '500px', margin: '0 1px', border: '1px solid #ccc', borderRadius: '5px', padding: '1px', marginInline: '10px' }}>
                                    <div style={{ textAlign: 'center' }}>
                                        <label style={{ fontWeight: 'bold', fontSize: '12px' }}>Grand Total: {totalPlanScore.toFixed(5)}</label>
                                    </div>
                                    <Bar
                                        data={reportScoreChartData}
                                        options={{
                                            responsive: true,
                                            plugins: {
                                                legend: {
                                                    position: 'top',
                                                },
                                                title: {
                                                    display: true,
                                                    text: 'Report Score by Plan',
                                                    font: {
                                                        weight: 'bold',
                                                    },
                                                    color: 'black',
                                                },
                                            },
                                            scales: {
                                                y: {
                                                    beginAtZero: true,
                                                    title: {
                                                        display: true,
                                                        text: 'Score',
                                                    },
                                                },
                                                x: {
                                                    title: {
                                                        display: true,
                                                        text: 'Plan',
                                                    },
                                                },
                                            },
                                        }}
                                    />
                                </div>
                            )}
                        </div>

                        {selectedReportCountData && (
                            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%', flexWrap: 'wrap' }}>
                                <div style={{ flex: '1 1 50%', maxWidth: '500px', margin: '0 1px', border: '1px solid #ccc', borderRadius: '5px', padding: '1px', marginInline: '10px' }}>
                                    <Line
                                        data={submissionNumberChartData}
                                        options={{
                                            ...barChartOptions,
                                            plugins: {
                                                ...barChartOptions.plugins,
                                                title: {
                                                    display: true, text: 'Submission Numbers by Plan',
                                                    font: {
                                                        weight: 'bold',
                                                    },
                                                    color: 'black',
                                                },
                                            },
                                            elements: {
                                                line: { tension: 0.4 },
                                                point: { radius: 5, hoverRadius: 7 },
                                            },
                                            scales: {
                                                y: {
                                                    beginAtZero: true,
                                                    ticks: { stepSize: 50 },
                                                    title: { display: true, text: 'Count' },
                                                },
                                                x: {
                                                    title: { display: true, text: 'Submission Numbers' },
                                                },
                                            },
                                        }}
                                    />
                                </div>

                                <div style={{ flex: '1 1 50%', maxWidth: '500px', margin: '0 1px', border: '1px solid #ccc', borderRadius: '5px', padding: '1px', marginInline: '10px' }}>
                                    <Line
                                        data={lastSubmissionStatusChartData}
                                        options={{
                                            ...barChartOptions,
                                            plugins: {
                                                ...barChartOptions.plugins,
                                                title: {
                                                    display: true, text: 'Last Submission Status by Plan',
                                                    font: {
                                                        weight: 'bold',
                                                    },
                                                    color: 'black',
                                                },
                                            },
                                            elements: {
                                                line: {
                                                    tension: 0.4, // Smooth curves
                                                },
                                                point: {
                                                    radius: 5, // Point size
                                                    hoverRadius: 7, // Hover point size
                                                },
                                            },
                                            scales: {
                                                y: {
                                                    beginAtZero: true,
                                                    ticks: {
                                                        stepSize: 50, // Adjust step size as needed
                                                    },
                                                    title: {
                                                        display: true,
                                                        text: 'Count', // Label for the y-axis
                                                    },
                                                },
                                                x: {
                                                    title: {
                                                        display: true,
                                                        text: 'Submission Status', // Label for the x-axis
                                                    },
                                                },
                                            },
                                        }}
                                    />
                                </div>

                                <div style={{ flex: '1 1 50%', maxWidth: '500px', margin: '0 1px', border: '1px solid #ccc', borderRadius: '5px', padding: '1px', marginInline: '10px' }}>
                                    <Line
                                        data={lastReviewStatusChartData}
                                        options={{
                                            ...barChartOptions,
                                            plugins: {
                                                ...barChartOptions.plugins,
                                                title: {
                                                    display: true, text: 'Last Review Status by Plan',
                                                    font: {
                                                        weight: 'bold',
                                                    },
                                                    color: 'black',
                                                },
                                            },
                                            elements: {
                                                line: {
                                                    tension: 0.4, // Smooth curves
                                                },
                                                point: {
                                                    radius: 5, // Point size
                                                    hoverRadius: 7, // Hover point size
                                                },
                                            },
                                            scales: {
                                                y: {
                                                    beginAtZero: true,
                                                    ticks: {
                                                        stepSize: 50, // Adjust step size as needed
                                                    },
                                                    title: {
                                                        display: true,
                                                        text: 'Count', // Label for the y-axis
                                                    },
                                                },
                                                x: {
                                                    title: {
                                                        display: true,
                                                        text: 'Review Status', // Label for the x-axis
                                                    },
                                                },
                                            },
                                        }}
                                    />
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>
            */}
            <Footer />
        </>
    );
};

export default Dashboard;